import React from "react";
import { Modal, Row, Col, Alert, Badge } from "reactstrap";
import { useTheme } from "../contexts/ThemeContext";

const VpsErrorsModal = ({
  showVpsErrors,
  setShowVpsErrors,
  vpsErrorMessages,
  isViewTxn = false,
  setAuthWithWarnings = () => {},
  setAuthModal = () => {},
}: {
  showVpsErrors: any;
  setShowVpsErrors: any;
  vpsErrorMessages: any;
  isViewTxn?: boolean;
  setAuthModal?: any;
  setAuthWithWarnings?: any;
}) => {
  const { theme } = useTheme();

  // Theme colors with proper dark mode support
  const themeColors = {
    bgPrimary: theme === "dark" ? "#1e1e2f" : "#ffffff",
    bgCard: theme === "dark" ? "#27293d" : "#f8f9fa",
    textPrimary: theme === "dark" ? "#ececec" : "#333333",
    textSecondary: theme === "dark" ? "#e0e0e0" : "#6c757d",
    borderColor: theme === "dark" ? "#404040" : "#dee2e6",
    hintBorder: theme === "dark" ? "#45b7d1" : "#17a2b8",
    badge: {
      dangerBg: "#dc3545",
      primaryBg: "#007bff",
    },
  };

  // Font sizing scale for consistency
  const fontSize = {
    xs: "0.75rem", // 12px - badges, tiny text
    sm: "0.875rem", // 14px - labels, secondary text
    base: "1rem", // 16px - body text
    md: "1.125rem", // 18px - section headers
    lg: "1.25rem", // 20px - modal titles
  };

  const formatCurrency = (value: number) => {
    if (value === undefined || value === null) return "—";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatUnits = (value: number) => {
    if (value === undefined || value === null) return "—";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    });
  };

  const DataItem = ({
    label,
    value,
    color,
  }: {
    label: string;
    value: any;
    color?: string;
  }) => (
    <div
      className="d-flex justify-content-between align-items-center mb-1 py-2 border-bottom"
      style={{
        fontSize: fontSize.sm,
        borderColor: themeColors.borderColor,
      }}
    >
      <span
        className="font-weight-bold"
        style={{ color: themeColors.textSecondary }}
      >
        {label}
      </span>
      <span
        style={{
          color: color || themeColors.textPrimary,
          fontWeight: 600,
          fontSize: fontSize.base,
        }}
      >
        {value}
      </span>
    </div>
  );

  const renderErrorContent = (error: any) => {
    const isCoa = !error.error_type;
    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <Badge
            color={isCoa ? "primary" : "danger"}
            pill
            style={{ fontSize: fontSize.xs }}
          >
            {isCoa ? "CoA" : (error.error_type || "ERROR").replace(/_/g, " ")}
          </Badge>
          <span
            className="font-weight-bold"
            style={{
              color: "#45b7d1",
              fontSize: fontSize.sm,
            }}
          >
            {error.fund_code}
          </span>
        </div>

        {error.error && (
          <p
            className="small font-italic mb-2"
            style={{
              color: "#ff6b6b",
              fontSize: fontSize.sm,
            }}
          >
            {error.error}
          </p>
        )}

        <div
          className="p-3 rounded shadow-sm"
          style={{
            backgroundColor: themeColors.bgCard,
            border: `1px solid ${themeColors.borderColor}`,
          }}
        >
          {/* Conditional Data Points - Compact Form */}
          {error.requested !== undefined && (
            <DataItem
              label="Requested"
              value={formatCurrency(error.requested)}
              color="#ff6b6b"
            />
          )}
          {error.available !== undefined && (
            <DataItem
              label="Available"
              value={formatCurrency(error.available)}
              color="#51cf66"
            />
          )}
          {error.requested_units !== undefined && (
            <DataItem
              label="Req. Units"
              value={formatUnits(error.requested_units)}
              color="#ff6b6b"
            />
          )}
          {error.available_units !== undefined && (
            <DataItem
              label="Avail. Units"
              value={formatUnits(error.available_units)}
              color="#51cf66"
            />
          )}
          {error.provided_units !== undefined && (
            <DataItem
              label="Req. Units"
              value={formatUnits(error.provided_units)}
              color="#ff6b6b"
            />
          )}
          {error.expected_units !== undefined && (
            <DataItem
              label="Avail. Units"
              value={formatUnits(error.expected_units)}
              color="#51cf66"
            />
          )}
          {error.provided_amount !== undefined && (
            <DataItem
              label="Provided"
              value={formatCurrency(error.provided_amount)}
            />
          )}
          {error.expected_amount !== undefined && (
            <DataItem
              label="Expected"
              value={formatCurrency(error.expected_amount)}
              color="#51cf66"
            />
          )}
          {error.nav !== undefined && (
            <DataItem label="NAV" value={Number(error.nav).toFixed(4)} />
          )}
        </div>

        {error.hint && (
          <div
            className="mt-3 p-2 font-italic"
            style={{
              borderLeft: `3px solid ${themeColors.hintBorder}`,
              backgroundColor:
                theme === "dark"
                  ? "rgba(69, 183, 209, 0.1)"
                  : "rgba(69, 183, 209, 0.05)",
              color: themeColors.textSecondary,
              fontSize: fontSize.sm,
            }}
          >
            {error.hint}
          </div>
        )}
        {error.message && (
          <div
            className="mt-3 p-2 font-italic"
            style={{
              borderLeft: `3px solid ${themeColors.hintBorder}`,
              backgroundColor:
                theme === "dark"
                  ? "rgba(69, 183, 209, 0.1)"
                  : "rgba(69, 183, 209, 0.05)",
              color: themeColors.textSecondary,
              fontSize: fontSize.sm,
            }}
          >
            {error.message}
          </div>
        )}
      </div>
    );
  };

  if (!showVpsErrors) return null;

  return (
    <Modal
      isOpen={showVpsErrors}
      toggle={() => setShowVpsErrors(false)}
      size="xl" // Increased size for better record viewing
      centered
      contentClassName={theme === "dark" ? "" : ""}
      style={{ color: themeColors.textPrimary }}
    >
      <div
        className="modal-header py-3"
        style={{
          backgroundColor: "#dc3545",
          borderBottom: `1px solid ${themeColors.borderColor}`,
        }}
      >
        <h5
          className="modal-title m-0"
          style={{ color: "#ffffff", fontSize: fontSize.lg }}
        >
          <i className="fa fa-exclamation-triangle mr-2"></i>
          VPS Validation Summary
        </h5>
        <button
          className="close"
          style={{ color: "#ffffff" }}
          onClick={() => setShowVpsErrors(false)}
        >
          &times;
        </button>
      </div>

      <div
        className="modal-body"
        style={{
          backgroundColor: themeColors.bgPrimary,
          color: themeColors.textPrimary,
          fontSize: fontSize.base,
        }}
      >
        <Alert
          color={isViewTxn ? "warning" : "danger"}
          className="py-2 mb-3"
          style={{ fontSize: fontSize.sm }}
        >
          {isViewTxn
            ? "Validation warnings found. Review data before authorization."
            : "Errors detected. Records have been updated with validation flags."}
        </Alert>

        <div
          style={{
            maxHeight: "65vh",
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px",
          }}
        >
          <Row>
            {vpsErrorMessages && vpsErrorMessages.length > 0 ? (
              vpsErrorMessages
                .flatMap((g: any) =>
                  g.messages && Array.isArray(g.messages) ? g.messages : g,
                )
                .map((error: any, index: number) => (
                  <Col md="6" key={index} className="mb-3">
                    <div
                      style={{
                        border: `1px solid ${themeColors.borderColor}`,
                        borderRadius: "8px",
                        backgroundColor: themeColors.bgCard,
                        color: themeColors.textPrimary,
                        height: "100%",
                      }}
                    >
                      {renderErrorContent(error)}
                    </div>
                  </Col>
                ))
            ) : (
              <Col className="text-center py-5">
                <p
                  style={{
                    color: themeColors.textSecondary,
                    fontSize: fontSize.base,
                  }}
                >
                  No detailed error logs found.
                </p>
              </Col>
            )}
          </Row>
        </div>
      </div>

      <div
        className="modal-footer py-3"
        style={{
          backgroundColor: themeColors.bgPrimary,
          borderTop: `1px solid ${themeColors.borderColor}`,
        }}
      >
        {isViewTxn && (
          <button
            className="btn btn-sm btn-primary"
            style={{ fontSize: fontSize.sm }}
            onClick={() => {
              setShowVpsErrors(false);
              setAuthWithWarnings(true);
              setAuthModal(true);
            }}
          >
            Authorize Anyway
          </button>
        )}
        <button
          className="btn btn-sm btn-secondary"
          style={{ fontSize: fontSize.sm }}
          onClick={() => setShowVpsErrors(false)}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default VpsErrorsModal;
